import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { CheckCircle2, LockKeyhole, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/SupabaseAuthContext';

export const SetPasswordPage = () => {
  const { session, loading, updatePassword } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // The user must already hold a session (established from the invite/recovery
  // link on the callback screen) to set a password.
  if (loading) return null;
  if (!session) return <Navigate to="/login" replace />;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Password must contain at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('The two passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      await updatePassword(password);
      navigate('/dashboard', { replace: true });
    } catch (submissionError) {
      setError(submissionError.message || 'Unable to set your password.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="bg-card border border-border rounded-2xl shadow-ambient p-8">
          <div className="flex flex-col items-center text-center mb-6">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-3">
              <ShieldCheck className="w-6 h-6 text-primary" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground">Set your password</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Welcome{session.user?.email ? `, ${session.user.email}` : ''}. Choose a password to
              finish activating your account.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">New password</Label>
              <div className="relative">
                <LockKeyhole className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  autoComplete="new-password"
                  className="pl-10"
                  minLength={8}
                  placeholder="At least 8 characters"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  data-testid="set-password-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm">Confirm password</Label>
              <div className="relative">
                <CheckCircle2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="confirm"
                  type="password"
                  autoComplete="new-password"
                  className="pl-10"
                  minLength={8}
                  placeholder="Re-enter your password"
                  value={confirm}
                  onChange={(event) => setConfirm(event.target.value)}
                  data-testid="confirm-password-input"
                />
              </div>
            </div>

            {error && (
              <p className="text-sm text-destructive" data-testid="set-password-error">
                {error}
              </p>
            )}

            <Button
              type="submit"
              className="w-full"
              disabled={submitting}
              data-testid="set-password-submit"
            >
              {submitting ? 'Saving...' : 'Set password & continue'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};
